import { apiClient, type Api } from "../../../services/api";

// Recommender v2 — Phase 0.3 (client). A session-level batcher for viewport
// "seen" impressions. useViewportImpression enqueues one payload per card that
// stayed >=1s in view; we POST them in batches (every FLUSH_INTERVAL_MS and on
// tab-hide/page-hide) instead of one request per card. Best-effort telemetry:
// a failed flush is dropped (apiClient already reports it via onRequestError),
// never retried — losing an impression is acceptable, unbounded growth is not.

export type ImpressionPayload =
  Api["post"]["/webapp/private/feed/impressions"]["input"]["json"]["impressions"][number];

const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH = 200;

let queue: ImpressionPayload[] = [];
let timer: ReturnType<typeof setInterval> | null = null;
let lifecycleBound = false;

function stopTimer(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

function bindLifecycleOnce(): void {
  if (lifecycleBound) return;
  lifecycleBound = true;
  // Flush when the tab is backgrounded / the page is being torn down (mobile
  // webview), so the last <5s of impressions are not lost on navigation away.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushImpressions();
  });
  window.addEventListener("pagehide", () => void flushImpressions());
}

export function enqueueImpression(payload: ImpressionPayload): void {
  queue.push(payload);
  bindLifecycleOnce();
  if (timer === null) {
    timer = setInterval(() => void flushImpressions(), FLUSH_INTERVAL_MS);
  }
  if (queue.length >= MAX_BATCH) void flushImpressions();
}

export async function flushImpressions(): Promise<void> {
  if (queue.length === 0) {
    stopTimer();
    return;
  }
  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(batch.length);
  if (queue.length === 0) stopTimer();
  try {
    await apiClient.post("/webapp/private/feed/impressions", {
      json: { impressions: batch },
    });
  } catch {
    // Best-effort: apiClient's onRequestError already reports failures to Sentry;
    // we intentionally do not requeue (a lost impression batch is acceptable).
  }
}
